// Building a chassis number, and refusing a model code that is pretending to be
// one. Shared by every door a vehicle can be created through.
//
// THE PROBLEM THIS EXISTS FOR
//
// A Japanese chassis number is a model code followed by a serial:
// `NZE141G-9185659`. The model code on its own — `NZE141G` — identifies the
// MODEL. Thousands of cars carry it, so a vehicle filed under one can never be
// matched back to anything: not to its logbook, not to a spec lookup, not to the
// next person who searches for it.
//
// fields.js cannot tell the difference. Its rule is 8 to 20 characters of
// letters, digits and hyphens, and `DBA-NZE141G` is eleven of exactly those. So a
// model code reached the database looking like a correct entry, which is why it
// went unnoticed until someone tried to use one of the records.
//
// Both halves of the answer live here so the two doors cannot drift apart:
//
//   ChassisComposer   — build it right. The catalogue's code is fixed as a
//                       prefix and only the SERIAL is asked for, which makes the
//                       wrong answer unrepresentable rather than merely
//                       detectable. Also less typing.
//   useModelCodeCheck — catch one typed by hand, pasted, or restored from an old
//                       draft, by asking the server whether it is a whole model
//                       code and nothing more.

import React, { useEffect, useState } from "react";
import { apiFetch } from "../api.js";

/**
 * Split a composed chassis number back into its prefix and serial.
 *
 * Derived on every render rather than held in state: the record is the one copy
 * of the value, and a second copy would drift the moment the field is edited from
 * anywhere else — a resumed draft, the escape hatch below, going back to re-pick
 * the variant.
 */
export function splitChassis(value, prefix) {
  const raw = String(value ?? "");
  if (!prefix) return { serial: raw, matches: false };
  //Compared case- and punctuation-insensitively, because the prefix is normalised
  //('NZE141G') while the record may hold what a person typed ('nze141g-9185659').
  const bare = (s) => s.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const flat = bare(raw);
  const head = bare(prefix);
  if (!flat.startsWith(head)) return { serial: raw, matches: false };
  //The separator is stepped over if it is there, and not required: a chassis
  //stored as 'NZE141G9185659' is the same number as 'NZE141G-9185659'.
  const rest = raw.slice(raw.toUpperCase().indexOf(head[0]) + prefix.length);
  return { serial: rest.replace(/^[^A-Za-z0-9]+/, ""), matches: true };
}

/**
 * Is what is in the box a model code and nothing else?
 *
 * Answered by the server against the ~15,000 real codes in carmakes
 * (GET /carmakes/chassis-check), so it is an exact match rather than a guess
 * about shape. Returns `{ key, makeModels }` when it is one, and null otherwise.
 *
 * AN ERROR CLEARS THE FLAG rather than raising one. carmakes is a SECOND
 * database and recording a vehicle is the primary job; if the lookup cannot be
 * reached the right outcome is the behaviour we had before it existed, not a
 * blocked form explaining a service the user has never heard of. That is also
 * why this is a lookup the client asks for rather than a rule inside
 * /addinventory — see routes/carmakesRoutes.js.
 */
export function useModelCodeCheck(chassisNo) {
  const [hit, setHit] = useState(null);

  useEffect(() => {
    const chassis = String(chassisNo || "").trim();
    //CLEARED BEFORE ASKING, not only when the answer comes back no. The lookup
    //is debounced, so between a keystroke and the reply 300ms later `hit` still
    //holds the verdict on the value that was REPLACED — and callers read this
    //state synchronously when a button is pressed.
    //
    //The deal modal's deliver gate makes that reachable in one motion: its
    //blockers dialog offers a Chassis No. box and a Deliver button side by side
    //and commits what is typed straight onto the record, so correcting the
    //value and confirming can easily happen inside the debounce window. The
    //dialog would then refuse the very value it had just been given.
    //
    //Fails OPEN, like the catch below and for the same reason: "we have not
    //been told this is wrong" is the right default for an advisory standing in
    //front of a second database. A model code that slips past one press is
    //named by the banner a moment later and refused on the next.
    setHit(null);
    if (chassis.length < 3) return undefined;
    let live = true;
    const t = setTimeout(() => {
      apiFetch(`/carmakes/chassis-check?q=${encodeURIComponent(chassis)}`, { method: "GET" })
        .then((r) => (r.ok ? r.json() : { modelCodeOnly: false }))
        .then((d) => { if (live) setHit(d?.modelCodeOnly ? d : null); })
        .catch(() => { if (live) setHit(null); });
    }, 300);
    //A slow answer for a chassis that has since been retyped must not overwrite
    //the answer for the current one.
    return () => { live = false; clearTimeout(t); };
  }, [chassisNo]);

  return hit;
}

/**
 * "That is a model code, not a chassis number." A BLOCK, not a warning.
 *
 * The certainty is what earns that. A duplicate chassis may legitimately be a
 * different car — four pairs collide in production already — so it can only be
 * pointed out. A value that matches carmakes exactly with no serial after it is
 * not a chassis number under any reading.
 *
 * Names the car the code belongs to, because that is what makes it recognisable:
 * someone holding a logbook can see at once whether "Toyota Corolla Fielder" is
 * the vehicle in front of them, and therefore where the rest of the number is.
 */
export function ModelCodeWarning({ hit }) {
  if (!hit) return null;
  const names = (hit.makeModels || [])
    .map((m) => [m.make, m.model].filter(Boolean).join(" "))
    .filter(Boolean);
  return (
    <div className="p-3 rounded-xl text-sm"
         style={{ background: "var(--theme-danger-tint)", border: "1px solid var(--theme-danger)",
                  color: "var(--theme-danger-ink)" }}>
      <strong>That is a model code, not this car's chassis number.</strong>
      <p className="mt-1">
        <span className="font-mono">{hit.key}</span> is the model code
        {names.length ? ` for the ${names.join(", ")}` : ""} — thousands of cars share it.
        The chassis number is that code plus the serial stamped after it, as it appears
        on the logbook: <span className="font-mono">{hit.key}-9185659</span>.
      </p>
    </div>
  );
}

/**
 * The chassis number, built rather than typed.
 *
 * The catalogue knows the model code, so it is shown as a fixed prefix and only
 * the serial is asked for. Seven characters off the logbook instead of fifteen,
 * and — the point — the model code cannot end up in the field on its own.
 *
 * THE ESCAPE HATCH IS NOT OPTIONAL. A car's paperwork does not always agree with
 * the catalogue: a re-registered import, an engine swap, a code carmakes files
 * under a different prefix. A composer with no way out would make those cars
 * unrecordable. Taking it CLEARS the field, so what follows is typed in full
 * against the ordinary rule rather than half-inherited from a guess.
 *
 * @param {string} prefix   the bare model code, e.g. 'NZE141G'. Falsy renders the
 *        plain input, which is what makes this safe to drop in unconditionally.
 * @param {Function} renderInput  ({ id, value, onChange, onKeyDown }) => node, so
 *        each caller keeps its own text input — the wizard commits on blur through
 *        fields.js, the picker holds plain local state. The composer owns the
 *        SHAPE of the answer; it does not own how a box is drawn.
 * @param {string} [idPrefix]  disambiguates the ids when two forms are mounted.
 */
export function ChassisComposer({
  prefix, value, onSet, onSubmit, renderInput,
  label = "Chassis No.", required = true, hint, idPrefix = "wiz",
}) {
  const { serial, matches } = splitChassis(value, prefix);
  //Free typing once the user has said the prefix is wrong, or once the value on
  //the record plainly is not built on it — a resumed draft, or a chassis pasted
  //in whole. Initialised once: flipping it on a later render would yank the box
  //out from under someone mid-edit.
  const [freeform, setFreeform] = useState(() => !!String(value ?? "") && !matches);

  const labelNode = (htmlFor) => (
    <label className="label-t block mb-0.5" htmlFor={htmlFor}>
      {label}{required && <span className="error-t ml-0.5">*</span>}
    </label>
  );

  if (!prefix || freeform) {
    return (
      <div>
        {labelNode(`${idPrefix}-ChassisNo`)}
        {renderInput({ id: `${idPrefix}-ChassisNo`, value: value ?? "" })}
        {hint && <p className="hint-t mt-0.5">{hint}</p>}
      </div>
    );
  }

  const serialId = `${idPrefix}-ChassisSerial`;
  return (
    <div>
      {labelNode(serialId)}
      <div className="flex items-stretch gap-0">
        <span className="px-2 flex items-center text-sm font-mono rounded-l-lg"
              style={{ background: "var(--theme-surface-alt)",
                       border: "1px solid var(--theme-line)", borderRight: "none",
                       color: "var(--theme-muted)" }}>
          {prefix}
        </span>
        <input id={serialId} type="text" className="input-t rounded-l-none"
               placeholder="9185659"
               value={serial}
               onChange={(e) => {
                 const typed = e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
                 //An empty serial commits an EMPTY chassis, never the bare prefix.
                 //Committing the prefix alone would put the model code in the field
                 //under a different name — the very thing this component exists to
                 //stop — and it would pass the length rule on the way through.
                 onSet(typed ? `${prefix}-${typed}` : "");
               }}
               onKeyDown={(e) => {
                 if (e.key !== "Enter") return;
                 e.preventDefault();
                 e.currentTarget.blur();
                 if (onSubmit) onSubmit();
               }} />
      </div>
      <p className="hint-t mt-0.5">
        {serial
          ? <>Saved as <span className="font-mono">{`${prefix}-${serial}`}</span>.</>
          : <>{prefix} is the model code from the catalogue. Add the serial that follows
              it on the logbook, e.g. 9185659.</>}
        {" "}
        <button type="button" className="underline"
                style={{ color: "var(--theme-accent-ink)" }}
                onClick={() => { setFreeform(true); onSet(""); }}>
          Type the whole number instead
        </button>
      </p>
    </div>
  );
}
